import { REVIEW_AREA_PERMISSIONS, useAuthStore } from '@/stores/auth';
import { isOfficeAddinHost } from '@/office/utils';
import { createAppHistory, officeRoutes, resolveOfficeRedirect } from '@/office/routes';
import { createRouter, type RouteRecordRaw } from 'vue-router';
import { setRouter } from './instance';
import type { UserPermissions } from '@/api';
import type { LayoutName } from '@/layouts';
import ContractManagementSignUp from '../components/ContractManagementSignUp.vue';
import Login from '../components/Login.vue';
import SignUp from '../components/SignUp.vue';
import SignUpCompleteView from '@/views/SignUpCompleteView.vue';
import VerifyEmailView from '@/views/VerifyEmailView.vue';
import InviteAcceptView from '@/views/InviteAcceptView.vue';

import AdminIntakeAgentLogsView from '@/views/AdminIntakeAgentLogsView.vue';
import AdminModelPricingView from '@/views/AdminModelPricingView.vue';
import AdminStatisticsView from '@/views/AdminStatisticsView.vue';
import AdminOrganizationsView from '@/views/AdminOrganizationsView.vue';
import AdminUsersView from '@/views/AdminUsersView.vue';
import ClauseLibraryView from '@/views/ClauseLibraryView.vue';
import ContractManagementCalendarView from '@/views/ContractManagementCalendarView.vue';
import ContractManagementCalendarDetailView from '@/views/ContractManagementCalendarDetailView.vue';
import CalendarWizardView from '@/views/contractManagement/CalendarWizardView.vue';
import ContractDetailView from '@/views/ContractDetailView.vue';
import SigningRequestDetailView from '@/views/signing/SigningRequestDetailView.vue';
import SigningRequestsView from '@/views/signing/SigningRequestsView.vue';
import ActionRequiredView from '@/views/ActionRequiredView.vue';
import SigningWizardView from '@/views/signing/SigningWizardView.vue';
import ContractFileDetailView from '@/views/ContractFileDetailView.vue';
import OrganisationMemoriesView from '@/views/OrganisationMemoriesView.vue';
import OrganisationSettingsView from '@/views/OrganisationSettingsView.vue';
import SystemFilesView from '@/views/SystemFilesView.vue';
import DocumentInsightDetailView from '@/views/DocumentInsightTableView.vue';
import IntakeAgentView from '@/views/IntakeAgent.vue';
import FileVaultView from '@/views/FileVaultView.vue';
import VaultFileVersionsView from '@/views/VaultFileVersionsView.vue';
import UploadHistoryView from '@/views/UploadHistoryView.vue';
import GaiaChatView from '@/components/GaiaChatView.vue';
import ChatView from '@/views/ChatView.vue';
import HomeView from '@/views/HomeView.vue';
import EntityListView from '@/views/contractManagement/EntityListView.vue';
import ContractManagementDetailView from '@/views/ContractManagementDetailView.vue';
import PromptCollectionDetailView from '@/views/PromptCollectionDetailView.vue';
import PromptCollectionsView from '@/views/PromptCollectionsView.vue';
import PromptDetailView from '@/views/PromptDetailView.vue';
import PromptLibraryView from '@/views/PromptLibraryView.vue';
import PlaybookClausesView from '@/views/PlaybookClausesView.vue';
import PlaybookEvaluationsView from '@/views/PlaybookEvaluationsView.vue';
import PlaybookUncommonsView from '@/views/PlaybookUncommonsView.vue';
import PlaybookExtractionsView from '@/views/PlaybookExtractionsView.vue';
import PlaybookGroupsView from '@/views/PlaybookGroupsView.vue';
import PlaybookCreateView from '@/views/PlaybookCreateView.vue';
import PlaybookEditView from '@/views/PlaybookEditView.vue';
import PlaybookRunsView from '@/views/PlaybookRunsView.vue';
import PlaybookRunDetailView from '@/views/PlaybookRunDetailView.vue';
import PlaybooksView from '@/views/PlaybooksView.vue';
import ContractTypesView from '@/views/ContractTypesView.vue';
import ContractTypeDetailView from '@/views/ContractTypeDetailView.vue';
import ExtractionPromptsView from '@/views/ExtractionPromptsView.vue';
import ReviewProjectsView from '@/views/ReviewProjectsView.vue';
import ReviewProjectDetailView from '@/views/ReviewProjectDetailView.vue';
import ReviewProjectStatisticsView from '@/views/ReviewProjectStatisticsView.vue';
import ReviewProjectTasksView from '@/views/ReviewProjectTasksView.vue';
import ReviewProjectSettingsView from '@/views/ReviewProjectSettingsView.vue';
import ReviewsView from '@/views/ReviewsView.vue';
import ReviewDetailView from '@/views/ReviewDetailView.vue';
import ServiceAccountTokenView from '@/views/ServiceAccountTokenView.vue';
import IntegrationOauthCallbackView from '@/views/IntegrationOauthCallbackView.vue';
import UsersView from '@/views/UsersView.vue';
import AgentTasksView from '@/views/AgentTasksView.vue';
import UserDetailView from '@/views/UserDetailView.vue';
import MemberDetailView from '@/views/MemberDetailView.vue';
import PermissionsView from '@/views/PermissionsView.vue';
import PermissionRoleDetailView from '@/views/PermissionRoleDetailView.vue';
import ContractManagementViews from '@/views/ContractManagementViews.vue';
import ContractManagementViewDetailView from '@/views/ContractManagementViewDetailView.vue';
import ContractManagementViewCreateView from '@/views/ContractManagementViewCreateView.vue';
import DraftsView from '@/views/contractGeneration/DraftsView.vue';
import TemplatesView from '@/views/contractGeneration/TemplatesView.vue';
import TemplateDetailView from '@/views/contractGeneration/TemplateDetailView.vue';
import TemplateAuthoringView from '@/views/contractGeneration/TemplateAuthoringView.vue';
import TemplateVariantDetailView from '@/views/contractGeneration/TemplateVariantDetailView.vue';
import SnippetLibraryView from '@/views/contractGeneration/SnippetLibraryView.vue';
import WizardView from '@/views/contractGeneration/WizardView.vue';
import { watch } from 'vue';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    requiresAdmin?: boolean;
    requiresOrgAdmin?: boolean;
    requiredPermissions?: (keyof UserPermissions)[];
    /** Passes when ANY of these holds (`requiredPermissions` needs all of them). */
    anyPermissions?: (keyof UserPermissions)[];
    layout?: LayoutName;
    collapseSidebar?: boolean;
    /**
     * Fully public route: the guard skips auth entirely, so the auth store is
     * never instantiated and Firebase never initializes. Stronger than
     * `requiresAuth: false` (login pages are unauthenticated but still need
     * the auth store to sign in / redirect signed-in users away).
     */
    public?: boolean;
    /**
     * Exempts the route from the guard's partial-auth-state redirects
     * (unverified → /verify-email, unprovisioned → /sign-up/complete). The
     * invite-accept page needs this: invited users are expected to be
     * unprovisioned and must not be bounced into the self-serve signup flow.
     */
    skipAuthStateRedirect?: boolean;
    /**
     * Show AuthLayout's brand panel. Only the two entry screens get it — login
     * and sign-up. Every later step in the flow (verify, profile, invite, the
     * CM funnel, reset, the OAuth callback) is somewhere you were sent, not
     * somewhere you chose to go, so it gets the mark above the card instead.
     */
    authBrand?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  ...officeRoutes,
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      layout: 'auth',
      authBrand: true,
    },
  },
  {
    path: '/sign-up',
    name: 'sign-up',
    component: SignUp,
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      layout: 'auth',
      authBrand: true,
    },
  },
  {
    path: '/sign-up/complete',
    name: 'sign-up-complete',
    component: SignUpCompleteView,
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      layout: 'auth',
    },
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: VerifyEmailView,
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      layout: 'auth',
    },
  },
  {
    path: '/invite/accept',
    name: 'invite-accept',
    component: InviteAcceptView,
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      layout: 'auth',
      skipAuthStateRedirect: true,
    },
  },
  {
    path: '/contract-management-sign-up',
    name: 'contract-management-sign-up',
    component: ContractManagementSignUp,
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      layout: 'auth',
    },
  },
  {
    path: '/cm',
    redirect: '/contract-management-sign-up',
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
    },
  },
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    // Everything waiting on the user, across their organisations.
    path: '/action-required',
    name: 'action-required',
    component: ActionRequiredView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/gaia-chat',
    name: 'gaia-chat',
    component: GaiaChatView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/chat',
    name: 'chat',
    component: ChatView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/vault',
    name: 'file-vault',
    component: FileVaultView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/vault/directory/:directoryUid',
    name: 'file-vault-directory',
    component: FileVaultView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/vault/file/:uid/versions',
    name: 'vault-file-versions',
    component: VaultFileVersionsView,
    meta: { requiresAuth: true, requiresAdmin: false },
  },
  {
    path: '/vault/upload-history',
    name: 'upload-history',
    component: UploadHistoryView,
    meta: { requiresAuth: true, requiresAdmin: false },
  },
  {
    path: '/reviews',
    name: 'reviews',
    component: ReviewsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      anyPermissions: REVIEW_AREA_PERMISSIONS,
    },
  },
  // The board and the table used to be two routes with two copies of the same
  // chrome; they are one view with a `?view=` switch now. Unnamed on purpose —
  // nothing resolves these by name — and the old query rides along, so a
  // bookmarked search or filter survives. A redirect resolves before the guards,
  // so the permission check comes from `/reviews`.
  {
    path: '/review-board',
    redirect: to => ({ name: 'reviews', query: { ...to.query, view: 'board' } }),
  },
  {
    path: '/reviews-table',
    redirect: to => ({ name: 'reviews', query: { ...to.query, view: 'table' } }),
  },
  {
    path: '/review-table/:id',
    name: 'review-table',
    component: DocumentInsightDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      anyPermissions: REVIEW_AREA_PERMISSIONS,
    },
  },
  {
    path: '/prompt-collections',
    name: 'prompt-collections',
    component: PromptCollectionsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/prompt-collections/:id',
    name: 'prompt-collection-detail',
    component: PromptCollectionDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/prompt-library',
    name: 'prompt-library',
    component: PromptLibraryView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/prompt-library/:collection',
    name: 'prompt-detail',
    component: PromptDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/clause-library',
    name: 'clause-library',
    component: ClauseLibraryView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/repository/contract-types',
    name: 'contract-types',
    component: ContractTypesView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/repository/contract-types/:uid',
    name: 'contract-type-detail',
    component: ContractTypeDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/repository/prompts',
    name: 'prompts',
    component: ExtractionPromptsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/repository/playbooks',
    name: 'playbooks',
    component: PlaybooksView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_playbooks'],
    },
  },
  {
    path: '/repository/review-projects',
    name: 'review-projects',
    component: ReviewProjectsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_review_projects'],
    },
  },
  {
    path: '/repository/review-projects/:uid',
    name: 'review-project-detail',
    component: ReviewProjectDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_review_projects'],
    },
  },
  // A project's sub pages: the workspace above is the project's page, and these
  // are reached from the gear menu beside its title (they were segment tabs of
  // the detail view before the redesign, so they need their own URLs now).
  {
    path: '/repository/review-projects/:uid/statistics',
    name: 'review-project-statistics',
    component: ReviewProjectStatisticsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_review_projects'],
    },
  },
  {
    path: '/repository/review-projects/:uid/tasks',
    name: 'review-project-tasks',
    component: ReviewProjectTasksView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_review_projects'],
    },
  },
  {
    path: '/repository/review-projects/:uid/settings',
    name: 'review-project-settings',
    component: ReviewProjectSettingsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_review_projects'],
    },
  },
  {
    path: '/repository/playbooks/new/:uid?',
    name: 'playbook-create',
    component: PlaybookCreateView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_create_playbooks'],
    },
  },
  {
    path: '/repository/playbooks/:uid/clauses',
    name: 'playbook-clauses',
    component: PlaybookClausesView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_playbooks'],
    },
  },
  {
    path: '/repository/playbooks/:uid/evaluations',
    name: 'playbook-evaluations',
    component: PlaybookEvaluationsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_playbooks'],
    },
  },
  {
    path: '/repository/playbooks/:uid/uncommons',
    name: 'playbook-uncommons',
    component: PlaybookUncommonsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_playbooks'],
    },
  },
  {
    path: '/repository/playbooks/:uid/extractions',
    name: 'playbook-extractions',
    component: PlaybookExtractionsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_playbooks'],
    },
  },
  {
    path: '/repository/playbooks/:uid/groups',
    name: 'playbook-groups',
    component: PlaybookGroupsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_playbooks'],
    },
  },
  {
    path: '/repository/playbooks/:uid/edit',
    name: 'playbook-edit',
    component: PlaybookEditView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_create_playbooks'],
    },
  },
  {
    path: '/repository/playbooks/:uid/runs',
    name: 'playbook-runs',
    component: PlaybookRunsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_playbooks'],
    },
  },
  {
    path: '/repository/playbooks/:uid/runs/:runUid',
    name: 'playbook-run-detail',
    component: PlaybookRunDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_playbooks'],
    },
  },
  {
    path: '/contract-management/calendar',
    name: 'contract-management-calendar',
    component: ContractManagementCalendarView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_contract_management'],
    },
  },
  {
    path: '/contract-management/calendar/new',
    name: 'contract-management-calendar-new',
    component: CalendarWizardView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      // Creating and updating a calendar are org-admin endpoints, so the whole
      // wizard is — the guard is the backstop behind the disabled buttons.
      requiresOrgAdmin: true,
      requiredPermissions: ['can_use_contract_management'],
    },
  },
  // Declared before `/calendar/:uid` — the detail route's param would
  // otherwise match `new` and `:uid/edit`'s prefix first.
  {
    path: '/contract-management/calendar/:uid/edit',
    name: 'contract-management-calendar-edit',
    component: CalendarWizardView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      // Creating and updating a calendar are org-admin endpoints, so the whole
      // wizard is — the guard is the backstop behind the disabled buttons.
      requiresOrgAdmin: true,
      requiredPermissions: ['can_use_contract_management'],
    },
  },
  {
    path: '/contract-management/calendar/:uid',
    name: 'contract-management-calendar-detail',
    component: ContractManagementCalendarDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_contract_management'],
    },
  },
  {
    path: '/contract-management/contracts/:uid',
    name: 'contract-management-contract-detail',
    component: ContractDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  // Signing: request list, wizard (new / from contract / edit) and detail.
  {
    path: '/signing',
    name: 'signing-requests',
    component: SigningRequestsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_signing'],
    },
  },
  {
    path: '/signing/new',
    name: 'signing-request-new',
    component: SigningWizardView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_signing'],
    },
  },
  {
    path: '/signing/contracts/:uid/new',
    name: 'signing-contract-request-new',
    component: SigningWizardView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_signing'],
    },
  },
  {
    path: '/signing/:signingRequestUid/edit',
    name: 'signing-request-edit',
    component: SigningWizardView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_signing'],
    },
  },
  {
    path: '/signing/:signingRequestUid',
    name: 'signing-request-detail',
    component: SigningRequestDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_signing'],
    },
  },
  {
    path: '/contract-management/contract-files/:uid',
    name: 'contract-management-contract-file-detail',
    component: ContractFileDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      collapseSidebar: true,
    },
  },
  {
    path: '/contract-templating/templates',
    name: 'contract-generation-templates',
    component: TemplatesView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_contract_generation'],
    },
  },
  {
    path: '/contract-templating/templates/:uid',
    name: 'contract-generation-template-detail',
    component: TemplateDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_contract_generation'],
    },
  },
  {
    path: '/contract-templating/templates/:uid/variant/:variantUid',
    name: 'contract-generation-template-variant-detail',
    component: TemplateVariantDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_contract_generation'],
    },
  },
  {
    path: '/contract-templating/templates/:uid/variant/:variantUid/author',
    name: 'contract-generation-template-author',
    component: TemplateAuthoringView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      collapseSidebar: true,
      requiredPermissions: ['can_use_contract_generation'],
    },
  },
  {
    path: '/contract-templating/snippets',
    name: 'contract-generation-snippet-library',
    component: SnippetLibraryView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_contract_generation'],
    },
  },
  {
    // The user's drafts — in progress and completed — with the "New document"
    // entry into the wizard.
    path: '/contract-templating/drafts',
    name: 'contract-generation-drafts',
    component: DraftsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_contract_generation'],
    },
  },
  // The three wizard routes collapse the sidebar: the compose step lays the
  // answer panel beside the full document, and every pixel of width is the
  // document's.
  {
    // One draft, open in the wizard: the URL a draft lives at once it exists.
    path: '/contract-templating/drafts/:draftUid',
    name: 'contract-generation-draft',
    component: WizardView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      collapseSidebar: true,
      requiredPermissions: ['can_use_contract_generation'],
    },
  },
  {
    path: '/contract-templating/new',
    name: 'contract-generation-wizard-new',
    component: WizardView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      collapseSidebar: true,
      requiredPermissions: ['can_use_contract_generation'],
    },
  },
  {
    path: '/contract-templating/new/:templateUid',
    name: 'contract-generation-wizard',
    component: WizardView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      collapseSidebar: true,
      requiredPermissions: ['can_use_contract_generation'],
    },
  },
  {
    path: '/settings',
    name: 'settings',
    redirect: '/settings/users',
  },
  {
    path: '/settings/users',
    name: 'settings-users',
    component: UsersView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiresOrgAdmin: true,
    },
  },
  {
    // The People page's Invitations tab — same view as Members, another tab.
    path: '/settings/invitations',
    name: 'settings-invitations',
    component: UsersView,
    props: { tab: 'invitations' },
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiresOrgAdmin: true,
    },
  },
  {
    path: '/settings/organisation',
    name: 'settings-organisation',
    component: OrganisationSettingsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiresOrgAdmin: true,
    },
  },
  {
    path: '/settings/agent-tasks',
    name: 'settings-agent-tasks',
    component: AgentTasksView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiresOrgAdmin: true,
    },
  },
  {
    path: '/users/me',
    name: 'settings-profile',
    component: UserDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/settings/users/:uid',
    name: 'user-detail',
    component: MemberDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiresOrgAdmin: true,
    },
  },
  {
    path: '/admin/statistics',
    name: 'admin-statistics',
    component: AdminStatisticsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/settings/admin-organizations',
    name: 'admin-organizations',
    component: AdminOrganizationsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/settings/admin-users',
    name: 'admin-users',
    component: AdminUsersView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  // Email agents were rebranded to intake agents; the old paths redirect so
  // bookmarks survive. Unnamed on purpose — nothing resolves these by name.
  {
    path: '/settings/admin-email-agent-logs',
    redirect: '/settings/admin-intake-agent-logs',
  },
  {
    path: '/settings/admin-intake-agent-logs',
    name: 'admin-intake-agent-logs',
    component: AdminIntakeAgentLogsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/settings/admin-model-pricing',
    name: 'admin-model-pricing',
    component: AdminModelPricingView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/settings/service-account-token',
    name: 'service-account-token',
    component: ServiceAccountTokenView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiresOrgAdmin: true,
    },
  },
  {
    // Path is dictated by the backend's external-access email links. The param
    // is the project's public external_access_uid, never the internal uid.
    path: '/external/review-projects/:externalUid',
    name: 'external-review-project',
    component: () => import('@/views/external/ExternalReviewProjectView.vue'),
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      layout: 'external',
      public: true,
    },
  },
  {
    path: '/oauth/callback/:provider',
    name: 'integration-oauth-callback',
    component: IntegrationOauthCallbackView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      layout: 'auth',
    },
  },
  {
    path: '/settings/memories',
    name: 'memories',
    component: OrganisationMemoriesView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiresOrgAdmin: true,
    },
  },
  {
    path: '/settings/system-files',
    name: 'settings-system-files',
    component: SystemFilesView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiresOrgAdmin: true,
    },
  },
  {
    path: '/settings/context-parameters',
    redirect: '/settings/memories',
  },
  {
    path: '/settings/permissions',
    name: 'settings-permissions',
    component: PermissionsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiresOrgAdmin: true,
    },
  },
  {
    path: '/settings/permissions/:uid',
    name: 'permission-role-detail',
    component: PermissionRoleDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiresOrgAdmin: true,
    },
  },
  {
    path: '/review-contract/:reviewId',
    name: 'review-contract',
    component: ReviewDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      anyPermissions: REVIEW_AREA_PERMISSIONS,
    },
  },
  {
    path: '/email-agents',
    redirect: '/intake-agents',
  },
  {
    path: '/intake-agents',
    name: 'intake-agents',
    component: IntakeAgentView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiresOrgAdmin: true,
    },
  },
  {
    path: '/contract-management/clause',
    name: 'contract-management-clause',
    component: EntityListView,
    // The entity list is one component for every type; the type is a prop
    // rather than a path segment so each entity keeps its own route and URL.
    props: { entityType: 'clause' },
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/contract-management/matter',
    name: 'contract-management-matter',
    component: EntityListView,
    props: { entityType: 'matter' },
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/contract-management/contractual-party',
    name: 'contract-management-contractual-party',
    component: EntityListView,
    props: { entityType: 'contractual-party' },
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/contract-management/:entityType/:uid/detail',
    name: 'contract-management-detail',
    component: ContractManagementDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/contract-management/obligations',
    name: 'contract-management-obligations',
    component: EntityListView,
    props: { entityType: 'obligation' },
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
    },
  },
  {
    path: '/contract-management-views',
    name: 'contract-management-views',
    component: ContractManagementViews,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_contract_management'],
    },
  },
  {
    path: '/contract-management-views/create',
    name: 'contract-management-view-create',
    component: ContractManagementViewCreateView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      requiredPermissions: ['can_use_contract_management'],
    },
  },
  {
    path: '/contract-management-view-detail/:viewId',
    name: 'contract-management-view-detail',
    component: ContractManagementViewDetailView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      collapseSidebar: true,
      requiredPermissions: ['can_use_contract_management'],
    },
  },
];

if (import.meta.env.DEV) {
  routes.push(
    {
      path: '/dev',
      name: 'dev',
      redirect: '/dev/websocket',
      meta: {
        requiresAuth: true,
        requiresAdmin: false,
      },
    },
    {
      path: '/dev/websocket',
      name: 'dev-websocket',
      component: () => import('@/views/WebsocketDebugView.vue'),
      meta: {
        requiresAuth: true,
        requiresAdmin: false,
      },
    },
    {
      path: '/dev/design',
      name: 'dev-design',
      component: () => import('@/views/DesignView.vue'),
      meta: {
        requiresAuth: true,
        requiresAdmin: false,
      },
    },
    {
      path: '/dev/icons',
      name: 'dev-icons',
      component: () => import('@/views/ContractStateIconsView.vue'),
      meta: {
        requiresAuth: true,
        requiresAdmin: false,
      },
    }
  );
}

// Catch-all, so it must stay last: any URL no route above matches. Behind
// sign-in like the rest of the app, so a stale link sends a signed-out visitor
// to login rather than to a bare 404.
routes.push({
  path: '/:pathMatch(.*)*',
  name: 'not-found',
  component: () => import('@/views/NotFoundView.vue'),
  meta: {
    requiresAuth: true,
  },
});

const router = createRouter({
  history: createAppHistory(import.meta.env.BASE_URL),
  routes,
});

// Let cycle-prone modules (tours, services) reach the router without importing
// this file — see src/router/instance.ts.
setRouter(router);

// Navigation guard
/**
 * Routes an org-less user may still visit: their own profile (personal
 * integrations live there), the OAuth return leg those integrations need, and
 * the dev-only workbench pages. Everything else is org-scoped.
 */
const ORGLESS_ALLOWED_ROUTES = new Set<string>([
  'home',
  'settings-profile',
  'integration-oauth-callback',
  'dev-design',
  'dev-icons',
  'dev-websocket',
  'not-found',
]);

router.beforeEach(async (to, _from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
  const requiresOrgAdmin = to.matched.some(record => record.meta.requiresOrgAdmin);
  const requiredPermissions = to.matched.flatMap(record => record.meta.requiredPermissions ?? []);

  // Fully public routes (e.g. external access links) bail out before touching
  // the auth store — instantiating it would initialize Firebase and fire
  // session-restore / backend-validation requests. The length check guards
  // `[].every()` being true; the catch-all means `matched` is never empty today.
  if (to.matched.length > 0 && to.matched.every(record => record.meta.public === true)) {
    next();
    return;
  }

  const authStore = useAuthStore();

  // Wait for the auth state to be ready
  if (!authStore.isReady) {
    // Create a promise that resolves when auth state is ready
    await new Promise<void>(resolve => {
      const unwatch = watch(
        () => authStore.isReady,
        ready => {
          if (ready) {
            unwatch();
            resolve();
          }
        },
        { immediate: true }
      );
    });
  }

  const isAuthenticated = authStore.isAuthenticated;

  const officeRedirect = resolveOfficeRedirect(to, isAuthenticated);

  // Partial-auth states: a Firebase session exists but the backend gate is
  // not passed yet. Such users are routed to the page that resolves their
  // state instead of being signed out (see stores/auth.ts). Routes flagged
  // skipAuthStateRedirect (invite-accept) handle these states themselves.
  const hasPartialAuthRedirect =
    authStore.user !== null && !isAuthenticated && !to.meta.skipAuthStateRedirect;
  // The pages that resolve partial states in turn need a Firebase session to
  // make sense at all.
  const onPartialAuthPage = to.name === 'verify-email' || to.name === 'sign-up-complete';

  if (officeRedirect) {
    next(officeRedirect);
  } else if (
    hasPartialAuthRedirect &&
    authStore.backendAuthState === 'unverified' &&
    to.name !== 'verify-email'
  ) {
    next('/verify-email');
  } else if (
    hasPartialAuthRedirect &&
    authStore.backendAuthState === 'unprovisioned' &&
    to.name !== 'sign-up-complete' &&
    to.name !== 'sign-up'
  ) {
    next('/sign-up/complete');
  } else if (
    onPartialAuthPage &&
    authStore.user === null &&
    // A verification-mail token is its own credential — the confirm endpoint
    // needs no session, so token-bearing visits stay on the page.
    !(to.name === 'verify-email' && typeof to.query.token === 'string')
  ) {
    // Preserve the ?verified flag so the login page can nudge.
    next({
      path: '/login',
      query: 'verified' in to.query ? { verified: to.query.verified } : {},
    });
  } else if (onPartialAuthPage && isAuthenticated) {
    next('/');
  } else if (requiresAuth && !isAuthenticated) {
    next(isOfficeAddinHost() ? '/office-login' : '/login');
  } else if (
    !requiresAuth &&
    isAuthenticated &&
    (to.name === 'login' || to.name === 'office-login')
  ) {
    next(isOfficeAddinHost() ? '/vault' : '/');
  } else if (
    isAuthenticated &&
    !authStore.hasActiveOrganisation &&
    !isOfficeAddinHost() &&
    to.path !== '/' &&
    !ORGLESS_ALLOWED_ROUTES.has(String(to.name))
  ) {
    // Every feature is org-scoped, so an org-less user has nowhere to go but
    // home, which lists their organisations and offers to create one. The
    // Office host is exempt because home itself forwards it to /vault.
    next('/');
  } else if (requiresAdmin && !authStore.userPermissions.is_platform_admin) {
    next('/');
  } else if (requiresOrgAdmin && !authStore.userPermissions.is_org_admin) {
    next('/');
  } else if (requiredPermissions.some(permission => !authStore.userPermissions[permission])) {
    next('/');
  } else if (
    to.matched.some(
      ({ meta }) =>
        meta.anyPermissions && !meta.anyPermissions.some(p => authStore.userPermissions[p])
    )
  ) {
    next('/');
  } else {
    next();
  }
});

export default router;
